import { SysCfgCreateRequestDto } from "@/shared/api";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai/index";
import { meStore } from "@/store/store";
import { z } from "zod";
import { isOwner } from "@/lib/auth-predicates";
import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import React, { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AutocompleteElement, TextFieldElement } from "react-hook-form-mui";
import { GameTypeConfig, gameTypeDefaultValues } from "@/module/service-configs-module/game-type-module/type";
import { ModalActionsButtons } from "@/components/modal-actions-buttons";


export default function GameTypeConfigModal({
                                        open,
                                        mode,
                                        onSave,
                                        onCancel,
                                        initialData = gameTypeDefaultValues
                                      }: {
  open: boolean;
  mode?: string;
  initialData?: GameTypeConfig;
  onSave?: (data: SysCfgCreateRequestDto) => void;
  onCancel?: () => void;
  confirmLoading?: boolean;
}) {
  const { t } = useTranslation();
  const [me] = useAtom(meStore);

  const gameTypeConfigSchema = z.object({
    name: z.string().min(1, t("fieldRequired")).trim(),
    auth_groups: isOwner(me)
      ? z.array(z.string())
      : z.array(z.string()).min(1, t("mustContainsGroup")),
    enabled: z.boolean().default(true),
  });


  const { data: authGroupsRs, isLoading: authGroupsIsLoading } = useAuthGroupsQuery({
    enabled: open,
  });

  const authGroupsOptions = useMemo(() => {
    return authGroupsRs?.data.map((it) => it.name).sort((a, b) => a.localeCompare(b)) ?? [];
  }, [authGroupsRs?.data]);


  const {
    control,
    reset,
    handleSubmit,
    formState: { errors , isDirty},
  } = useForm<GameTypeConfig>({
    mode: "onTouched",
    resolver: zodResolver(gameTypeConfigSchema),
    defaultValues: {...initialData}
  });

  useEffect(() => {
    reset();
  }, [open, reset]);

  const onSubmit: SubmitHandler<GameTypeConfig> = (data) => {

    const result = {
      ...data,
      auth_groups: new Set(data.auth_groups),
    };

    if (!onSave) return;
    onSave(result);

    onCancel && onCancel(); //purifying form and close dialog
  };

  return (
    <>
      <Dialog open={open} key="game-type-modal"
              onClose={(event, reason) => {
                if (reason === 'backdropClick' && isDirty && !window.confirm("Data has been changed. Do you want discard changes?")) return; //ask question if changes==true and click outSide
                onCancel?.();
              }}
      >
        <DialogTitle>
          {t("addNewGameType")}
          <IconButton
            aria-label="close"
            onClick={() => onCancel && onCancel()}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px" /* Расстояние между элементами */,
              width: "500px" /* Ширина формы */,
            }}
          >
            <AutocompleteElement
              control={control}
              loading={authGroupsIsLoading}
              name="auth_groups"
              multiple
              options={authGroupsOptions ?? []}
              label={t("authGroups")}
              textFieldProps={{
                error: !!errors.auth_groups,
                helperText: errors.auth_groups?.message ?? " ",
                variant: "standard",
              }}
            />

            <TextFieldElement
              name="name"
              control={control}
              label={t("name")}
              placeholder={t("name")}
              fullWidth
              error={!!errors.name?.message}
              helperText={errors.name?.message}
              slotProps={
                {
                  input: {
                    disabled: mode === "edit",
                  },
                }
              }
            />

            <ModalActionsButtons
              onCancelClick={() => onCancel && onCancel()}
              onConfirmClick={() => void(0)}
              //loading={confirmLoading}
            />
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}